import { Award, FolderOpen, History, UserRound, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useKidClassId } from '@/auth/authStore';
import { isStudentManager } from '@/auth/familyManager';
import { isStudentFamily } from '@/auth/studentAccount';
import { useMe } from '@/auth/useAuth';
import { LearnPageHeading } from '@/app/LearnPageHeading';
import { HangarDownloadCard } from './HangarDownloadCard';
import { ParentNeededNotice } from './student/ParentNeededNotice';

// An independent student manages their own purchases, Stars and reports
// (student-self-signup-prd.md §17, D-SSU-23).
const ACCOUNT_DESTINATION = {
  to: '/learn/account',
  title: 'My account',
  description: 'Buy courses and Study, Stars, orders and reports.',
  icon: Wallet,
  color: 'bg-wash-coral',
};

const destinations = [
  {
    to: '/learn/projects',
    title: 'My works',
    description: 'Your saved creations and drafts.',
    icon: FolderOpen,
    color: 'bg-wash-mint',
  },
  {
    to: '/learn/passport',
    title: 'Creator Passport',
    description: 'Your progress, skills and achievements.',
    icon: Award,
    color: 'bg-wash-sunshine',
  },
  {
    to: '/learn/workspace',
    title: 'AI sessions',
    description: 'Return to your previous AI conversations.',
    icon: History,
    color: 'bg-wash-sky',
  },
  {
    to: '/learn/profile',
    title: 'My profile',
    description: 'Your avatar and sign-in details.',
    icon: UserRound,
    color: 'bg-wash-bubblegum',
  },
];

export function MyPage() {
  const me = useMe('kid');
  const kidClassId = useKidClassId();
  const items = isStudentManager(me.data, kidClassId)
    ? [ACCOUNT_DESTINATION, ...destinations]
    : destinations;
  return (
    <div>
      <LearnPageHeading
        title="My space"
        description="Find your work, your progress and your profile."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map(({ to, title, description, icon: Icon, color }) => (
          <Link
            key={to}
            to={to}
            className="flex items-start gap-4 rounded-3xl border border-hairline bg-canvas-pure p-5 shadow-card-soft focus-visible:ring-2 focus-visible:ring-brand-coral"
          >
            <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${color}`}>
              <Icon aria-hidden="true" size={24} />
            </span>
            <div>
              <h2 className="text-lg font-bold text-ink">{title}</h2>
              <p className="mt-1 text-sm text-ink-soft">{description}</p>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-7">
        {/* Hangar pairing needs a parent (student-self-signup-prd.md Q4). */}
        {isStudentFamily(me.data) ? (
          <ParentNeededNotice feature="hangar" />
        ) : (
          <HangarDownloadCard age={me.data?.kind === 'kid' ? me.data.age : undefined} />
        )}
      </div>
    </div>
  );
}
